import { Flex, TagBadge, Text } from '@chatsdet/ui';
import type { ConvergenceRunHistoryRow } from '../hooks/useWorkflowRuns';
import { formatRelativeTime } from './run-formatters';
import { formatUpdatedAt } from '../utils';
import {
  CONVERGENCE_OUTCOME_VARIANTS,
  formatConvergenceLabel,
  formatConvergenceProgress,
} from './convergence-run-formatters';

const statusDotClass: Record<'success' | 'warning' | 'destructive' | 'neutral', string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  destructive: 'bg-destructive',
  neutral: 'bg-muted-foreground/60',
};

/**
 * An optimization run's row in the flow's Runs tab history, interleaved
 * chronologically with native `RunHistoryCard` rows. Same visual anatomy
 * (status dot, primary label, secondary detail line, right-aligned relative
 * timestamp) but its own vocabulary — a convergence run has no pass/fail
 * counts, it advances through phases and is judged by `outcome` — plus a
 * gray "Convergence" tag so its kind is unmistakable in a mixed list. No
 * delete menu: convergence runs have no delete semantics from a run list.
 */
export function ConvergenceRunHistoryCard({
  run,
  isSelected,
  onSelect,
}: {
  run: ConvergenceRunHistoryRow;
  isSelected: boolean;
  onSelect: (runId: string) => void;
}) {
  const isRunning = run.status === 'queued' || run.status === 'running';
  const primaryLabel =
    !isRunning && run.outcome
      ? formatConvergenceLabel(run.outcome)
      : formatConvergenceLabel(run.status);
  const dotVariant =
    !isRunning && run.outcome
      ? (CONVERGENCE_OUTCOME_VARIANTS[run.outcome] ?? 'warning')
      : 'neutral';
  const secondaryLabel = formatConvergenceProgress(run.phase, run.cycleCount, run.cycleCap);

  const primaryTimestamp = run.finishedAt ?? run.startedAt ?? run.createdAt;

  return (
    <div
      role="button"
      tabIndex={0}
      className={[
        'w-full text-left px-4 py-2.5 transition-colors cursor-pointer',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
        isSelected ? 'bg-muted/50' : 'hover:bg-muted/30',
      ].join(' ')}
      onClick={() => onSelect(run.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(run.id);
        }
      }}
    >
      <Flex justify="between" align="center" gap="sm">
        <Flex align="center" gap="sm" className="min-w-0 flex-1">
          <span
            className={[
              'h-2 w-2 shrink-0 rounded-full',
              statusDotClass[dotVariant],
              isRunning ? 'animate-pulse' : '',
            ].join(' ')}
            title={primaryLabel}
          />
          <Text size="sm" className="shrink-0">
            {primaryLabel}
          </Text>
          <Text size="xs" className="text-muted-foreground truncate">
            {secondaryLabel}
          </Text>
          <TagBadge name="Optimization" color="gray" size="sm" hideDot className="shrink-0" />
        </Flex>

        <Flex align="center" gap="xs" className="shrink-0">
          <Text
            size="xs"
            className="text-muted-foreground"
            title={formatUpdatedAt(primaryTimestamp)}
          >
            {formatRelativeTime(primaryTimestamp)}
          </Text>
        </Flex>
      </Flex>
    </div>
  );
}
